import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, CircularProgress, ToggleButtonGroup, ToggleButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Snackbar, Alert, IconButton, Tooltip, InputAdornment, Avatar, AvatarGroup,
    Autocomplete
} from '@mui/material'
import { Add, Edit, ContentCopy, Search as SearchIcon, CloudUpload, Close } from '@mui/icons-material'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { Api, numberWithCommas } from '../../helpers'
import { uploadToSpaces, fileToBase64 } from '../../helpers/uploadToSpaces'
import { Connect, mapStateToProps } from '../../Redux'
import { UserProps } from '../../types/user'
import { Sponsor } from '../Sponsors'

type EventFilter = 'Upcoming' | 'Ongoing' | 'Completed' | 'All'

interface EventRow {
    _id: string
    title: string
    poster?: string
    venue: string
    date: string
    time: string
    contributionPerMember: number
    maxParticipants: number
    participantsCount: number
    totalContributions: number
    status: 'upcoming' | 'ongoing' | 'completed'
    rewards?: { first: number, second: number, third: number, participation: number }
    rulesVersion?: 'old' | 'new'
    // Already resolved server-side (events.ts getEvents) into the full sponsor + this event's
    // own amountContributed - not just bare ids, so the table can render logos directly.
    sponsors?: { _id: string, name: string, banner: string, amountContributed: number }[]
}

interface EventSponsorEntry {
    sponsorId: string
    // A string (not number) like every other amount field in this form (contributionPerMember,
    // the reward fields) - kept as raw text while typing, parsed server-side.
    amountContributed: string
}

interface FormState {
    title: string
    venue: string
    date: string
    time: string
    contributionPerMember: string
    maxParticipants: string
    firstReward: string
    secondReward: string
    thirdReward: string
    participationReward: string
    rulesVersion: 'old' | 'new'
    sponsors: EventSponsorEntry[]
    posterFile: File | null
    posterPreview: string | null
    existingPoster: string | null
}

interface StateProps {
    events: EventRow[]
    sponsors: Sponsor[]
    loading: boolean
    filter: EventFilter
    searchVal: string
    showModal: boolean
    editingEventId: string | null
    saving: boolean
    form: FormState
    error: string | null
}

const defaultForm: FormState = {
    title: '',
    venue: '',
    date: '',
    time: '',
    contributionPerMember: '',
    maxParticipants: '',
    firstReward: '',
    secondReward: '',
    thirdReward: '',
    participationReward: '',
    rulesVersion: 'new',
    sponsors: [],
    posterFile: null,
    posterPreview: null,
    existingPoster: null
}

const filters: EventFilter[] = ['Upcoming', 'Ongoing', 'Completed', 'All']
const headers = ['Poster', 'Title', 'Date', 'Venue', 'Participants', 'Contribution', 'Sponsors', 'Status', 'Actions']

const STATUS_COLORS: Record<string, { bg: string, fg: string }> = {
    upcoming: { bg: '#dcfce7', fg: '#166534' },
    ongoing: { bg: '#dbeafe', fg: '#1e40af' },
    completed: { bg: '#f3f4f6', fg: '#6b7280' }
}

interface Props {
    User?: UserProps
}

const Events: FC<Props> = ({ User }) => {
    const currency = User?.currency ?? 'KES'
    const [state, setState] = useState<StateProps>({
        events: [],
        sponsors: [],
        loading: true,
        filter: 'Upcoming',
        searchVal: '',
        showModal: false,
        editingEventId: null,
        saving: false,
        form: defaultForm,
        error: null
    })
    const { events, sponsors, loading, filter, searchVal, showModal, editingEventId, saving, form, error } = state
    const posterInputRef = useRef<HTMLInputElement>(null)

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    // Server (see events.ts getEvents) doesn't take a filter/search param - it always returns the
    // full list with a computed `status`, so filtering by tab and by search text both just happen
    // client-side against what's already in hand (same as Rankings' own country/name filters).
    const getEvents = () => {
        setState(s => ({ ...s, loading: true }))
        Api({ controller: 'Events', action: 'getEvents' })
            .then((resp: any) => setState(s => ({ ...s, events: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const getSponsors = () => {
        Api({ controller: 'Sponsors', action: 'getSponsors' })
            .then((resp: any) => setState(s => ({ ...s, sponsors: resp || [] })))
            .catch(() => { })
    }

    const filtered = useMemo(() => {
        let list = events
        if (filter !== 'All') list = list.filter(ev => ev.status === filter.toLowerCase())
        if (searchVal.trim()) {
            const val = searchVal.toLowerCase()
            list = list.filter(ev => ev.title.toLowerCase().includes(val) || ev.venue.toLowerCase().includes(val))
        }
        return list
    }, [events, filter, searchVal])

    const debouncedSearch = useCallback(_.debounce((val: string) => setState(s => ({ ...s, searchVal: val })), 300), [])

    const sponsorsById = useMemo(() => new Map(sponsors.map(s => [s._id, s])), [sponsors])

    const openAdd = () =>
        setState(s => ({ ...s, showModal: true, editingEventId: null, form: defaultForm }))

    const openEdit = (ev: EventRow) =>
        setState(s => ({
            ...s,
            showModal: true,
            editingEventId: ev._id,
            form: {
                title: ev.title,
                venue: ev.venue,
                date: ev.date?.split('T')[0] ?? '',
                time: ev.time,
                contributionPerMember: String(ev.contributionPerMember ?? ''),
                maxParticipants: String(ev.maxParticipants ?? ''),
                firstReward: ev.rewards?.first ? String(ev.rewards.first) : '',
                secondReward: ev.rewards?.second ? String(ev.rewards.second) : '',
                thirdReward: ev.rewards?.third ? String(ev.rewards.third) : '',
                participationReward: ev.rewards?.participation ? String(ev.rewards.participation) : '',
                rulesVersion: ev.rulesVersion ?? 'new',
                sponsors: (ev.sponsors ?? []).map(s => ({ sponsorId: s._id, amountContributed: s.amountContributed ? String(s.amountContributed) : '' })),
                posterFile: null,
                posterPreview: null,
                existingPoster: ev.poster ?? null
            }
        }))

    // Purely a client-side prefill of the Add form - there's no duplicateEvent action, this just
    // saves re-typing everything for a similar event (fresh date/time, new sponsor pick is fine
    // to carry over since it's the same tournament series more often than not).
    const openDuplicate = (ev: EventRow) =>
        setState(s => ({
            ...s,
            showModal: true,
            editingEventId: null,
            form: {
                ...defaultForm,
                title: `${ev.title} (Copy)`,
                venue: ev.venue,
                contributionPerMember: String(ev.contributionPerMember ?? ''),
                maxParticipants: String(ev.maxParticipants ?? ''),
                firstReward: ev.rewards?.first ? String(ev.rewards.first) : '',
                secondReward: ev.rewards?.second ? String(ev.rewards.second) : '',
                thirdReward: ev.rewards?.third ? String(ev.rewards.third) : '',
                participationReward: ev.rewards?.participation ? String(ev.rewards.participation) : '',
                rulesVersion: ev.rulesVersion ?? 'new',
                sponsors: (ev.sponsors ?? []).map(s => ({ sponsorId: s._id, amountContributed: s.amountContributed ? String(s.amountContributed) : '' }))
            }
        }))

    const handlePosterSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = ev => setForm({ posterFile: file, posterPreview: ev.target?.result as string })
        reader.readAsDataURL(file)
        e.target.value = ''
    }

    const saveEvent = async () => {
        setState(s => ({ ...s, saving: true }))
        try {
            let poster = form.existingPoster || undefined
            if (form.posterFile) {
                const base64File = await fileToBase64(form.posterFile)
                const [newUrl] = await uploadToSpaces([base64File], 'event_posters')
                poster = newUrl
            }

            const resp: any = await Api({
                controller: 'Events',
                action: editingEventId ? 'updateEvent' : 'createEvent',
                ...(editingEventId && { eventId: editingEventId }),
                title: form.title,
                venue: form.venue,
                date: form.date,
                time: form.time,
                poster,
                contributionPerMember: form.contributionPerMember,
                maxParticipants: form.maxParticipants,
                firstReward: form.firstReward,
                secondReward: form.secondReward,
                thirdReward: form.thirdReward,
                participationReward: form.participationReward,
                rulesVersion: form.rulesVersion,
                sponsors: form.sponsors.map(s => ({ sponsorId: s.sponsorId, amountContributed: s.amountContributed }))
            })

            if (resp?.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false, editingEventId: null, form: defaultForm }))
                getEvents()
            }
        } catch (err: any) {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' }))
        }
    }

    const canSave = !saving && !!form.title && !!form.venue && !!form.date && !!form.time
        && (!!editingEventId || (!!form.contributionPerMember && !!form.maxParticipants))

    useEffect(() => {
        getEvents()
        getSponsors()
    }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Events</Typography>
            </Box>

            {/* Controls row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '5px', mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(_e, val) => val && setState(s => ({ ...s, filter: val }))}
                    size='small'
                    sx={{ bgcolor: '#fff', borderRadius: 2 }}
                >
                    {filters.map(f => (
                        <ToggleButton
                            key={f}
                            value={f}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2.5,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }
                            }}
                        >
                            {f}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <TextField
                    size='small'
                    placeholder='Search events...'
                    onChange={e => debouncedSearch(e.target.value)}
                    sx={{ flex: 1, minWidth: 180, bgcolor: '#fff', borderRadius: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Button
                    variant='contained'
                    startIcon={<Add />}
                    onClick={openAdd}
                    sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                >
                    Add Event
                </Button>
            </Box>

            {/* Table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ mx: '5px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                {headers.map(h => (
                                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                        No {filter === 'All' ? '' : filter.toLowerCase()} events found.
                                    </TableCell>
                                </TableRow>
                            ) : filtered.map(ev => {
                                const statusColor = STATUS_COLORS[ev.status] ?? STATUS_COLORS.upcoming
                                // Already the full {name, banner} from the server (events.ts getEvents'
                                // sponsors[].sponsorId lookup) - no need to resolve against the sponsors
                                // roster by id here.
                                const eventSponsors = ev.sponsors ?? []
                                return (
                                    <TableRow key={ev._id} hover>
                                        <TableCell>
                                            {ev.poster
                                                ? <Avatar src={ev.poster} variant='rounded' sx={{ width: 48, height: 48 }} />
                                                : <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</Typography>}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{ev.title}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#374151' }}>
                                            {new Date(ev.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>{ev.time}</Typography>
                                        </TableCell>
                                        <TableCell>{ev.venue}</TableCell>
                                        <TableCell>{ev.participantsCount ?? 0}/{ev.maxParticipants || '—'}</TableCell>
                                        <TableCell>{currency} {numberWithCommas(ev.contributionPerMember ?? 0)}</TableCell>
                                        <TableCell>
                                            {eventSponsors.length ? (
                                                // Default object-fit:cover crops a wide wordmark/tall badge logo
                                                // to fill the square - contain + a white backing (see Sponsors
                                                // page's own table) shows each one whole instead.
                                                <AvatarGroup
                                                    max={3}
                                                    sx={{
                                                        '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.65rem', border: '2px solid #fff', bgcolor: '#fff' },
                                                        '& .MuiAvatar-img': { objectFit: 'contain', padding: '3px' }
                                                    }}
                                                >
                                                    {eventSponsors.map(s => (
                                                        <Tooltip
                                                            key={s._id}
                                                            title={s.amountContributed ? `${s.name} · ${currency} ${numberWithCommas(s.amountContributed)}` : s.name}
                                                        >
                                                            <Avatar src={s.banner} variant='rounded' />
                                                        </Tooltip>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ev.status[0].toUpperCase() + ev.status.slice(1)}
                                                size='small'
                                                sx={{ bgcolor: statusColor.bg, color: statusColor.fg, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title='Edit'>
                                                <IconButton size='small' onClick={() => openEdit(ev)} sx={{ color: 'primary.main' }}>
                                                    <Edit fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Duplicate'>
                                                <IconButton size='small' onClick={() => openDuplicate(ev)} sx={{ color: '#6b7280' }}>
                                                    <ContentCopy fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add / Edit Dialog */}
            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingEventId ? 'Edit Event' : 'Add Event'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label='Event Title'
                        value={form.title}
                        onChange={e => setForm({ title: e.target.value })}
                        fullWidth size='small'
                    />
                    <TextField
                        label='Venue'
                        value={form.venue}
                        onChange={e => setForm({ venue: e.target.value })}
                        fullWidth size='small'
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label='Date'
                            type='date'
                            value={form.date}
                            onChange={e => setForm({ date: e.target.value })}
                            fullWidth size='small'
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label='Time'
                            placeholder='e.g. 2:00 PM'
                            value={form.time}
                            onChange={e => setForm({ time: e.target.value })}
                            fullWidth size='small'
                        />
                    </Box>

                    {/* Locked once created - joinEvent already charges people the amount agreed
                        to at the time they joined, so changing this after the fact would make
                        that price a lie for anyone already in. Same rule the mobile app's own
                        AddEvent form applies (see lockedFields there). */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={`Contribution Per Member (${currency})`}
                            type='number'
                            value={form.contributionPerMember}
                            onChange={e => setForm({ contributionPerMember: e.target.value })}
                            fullWidth size='small'
                            disabled={Boolean(editingEventId)}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label='Max Participants'
                            type='number'
                            value={form.maxParticipants}
                            onChange={e => setForm({ maxParticipants: e.target.value })}
                            fullWidth size='small'
                            disabled={Boolean(editingEventId)}
                            inputProps={{ min: 1 }}
                        />
                    </Box>

                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', mt: 0.5 }}>
                        Rewards (optional) - paid out minus SpotPool's 5% fee
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={`1st Place (${currency})`}
                            type='number'
                            value={form.firstReward}
                            onChange={e => setForm({ firstReward: e.target.value })}
                            fullWidth size='small'
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label={`2nd Place (${currency})`}
                            type='number'
                            value={form.secondReward}
                            onChange={e => setForm({ secondReward: e.target.value })}
                            fullWidth size='small'
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={`3rd Place (${currency})`}
                            type='number'
                            value={form.thirdReward}
                            onChange={e => setForm({ thirdReward: e.target.value })}
                            fullWidth size='small'
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label={`Participation Token (${currency})`}
                            type='number'
                            value={form.participationReward}
                            onChange={e => setForm({ participationReward: e.target.value })}
                            fullWidth size='small'
                            inputProps={{ min: 0 }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Rules</Typography>
                        <ToggleButtonGroup
                            value={form.rulesVersion}
                            exclusive
                            size='small'
                            onChange={(_e, val) => val && setForm({ rulesVersion: val })}
                        >
                            <ToggleButton value='new' sx={{ textTransform: 'none', px: 2 }}>New Rules</ToggleButton>
                            <ToggleButton value='old' sx={{ textTransform: 'none', px: 2 }}>Old Rules</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        {/* Picks one sponsor at a time (not a multi-select) - each pick needs its
                            own amount right after, so a bare multi-select tag list would still
                            need a second step per sponsor to enter that. Already-added sponsors
                            drop out of the options so the same one can't be added twice. */}
                        <Autocomplete
                            size='small'
                            options={sponsors.filter(s => !form.sponsors.some(fs => fs.sponsorId === s._id))}
                            getOptionLabel={s => s.name}
                            value={null}
                            onChange={(_e, picked) => picked && setForm({ sponsors: [...form.sponsors, { sponsorId: picked._id, amountContributed: '' }] })}
                            renderOption={(props, s) => (
                                <Box component='li' {...props} key={s._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    {/* Same "whole logo on a neutral card" treatment as the Sponsors
                                        page's own table (see Sponsors/index.tsx) - a plain Avatar's
                                        object-fit:cover cropped wide wordmarks/tall badges down to an
                                        unrecognizable sliver. */}
                                    <Box sx={{ width: 32, height: 22, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Box component='img' src={s.banner} alt={s.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                    </Box>
                                    {s.name}
                                </Box>
                            )}
                            renderInput={params => (
                                <TextField {...params} label='Add a Sponsor' placeholder={sponsors.length ? 'Search sponsors...' : 'No sponsors added yet'} />
                            )}
                        />

                        {form.sponsors.length > 0 && (
                            <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {form.sponsors.map((entry, i) => {
                                    const sponsor = sponsorsById.get(entry.sponsorId)
                                    if (!sponsor) return null
                                    return (
                                        <Box key={entry.sponsorId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
                                            <Box sx={{ width: 40, height: 26, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Box component='img' src={sponsor.banner} alt={sponsor.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                            </Box>
                                            <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }} noWrap>{sponsor.name}</Typography>
                                            <TextField
                                                size='small'
                                                label='Amount Contributed'
                                                type='number'
                                                value={entry.amountContributed}
                                                onChange={e => {
                                                    const next = [...form.sponsors]
                                                    next[i] = { ...next[i], amountContributed: e.target.value }
                                                    setForm({ sponsors: next })
                                                }}
                                                InputProps={{ startAdornment: <InputAdornment position='start'>{currency}</InputAdornment> }}
                                                inputProps={{ min: 0 }}
                                                sx={{ width: 190 }}
                                            />
                                            <IconButton
                                                size='small'
                                                onClick={() => setForm({ sponsors: form.sponsors.filter((_, idx) => idx !== i) })}
                                            >
                                                <Close fontSize='small' />
                                            </IconButton>
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}
                    </Box>

                    {/* Poster Upload */}
                    <Box>
                        <input
                            ref={posterInputRef}
                            type='file'
                            accept='image/*'
                            style={{ display: 'none' }}
                            onChange={handlePosterSelect}
                        />
                        <Button
                            variant='outlined'
                            startIcon={<CloudUpload />}
                            onClick={() => posterInputRef.current?.click()}
                            sx={{ borderRadius: 2, borderStyle: 'dashed', textTransform: 'none' }}
                            fullWidth
                        >
                            {form.posterPreview || form.existingPoster ? 'Replace Poster' : 'Upload Poster'}
                        </Button>
                        {(form.posterPreview || form.existingPoster) && (
                            <Box sx={{ mt: 1.5 }}>
                                <Box
                                    component='img'
                                    src={form.posterPreview || form.existingPoster || undefined}
                                    alt='Event poster'
                                    sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveEvent}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingEventId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Events)
